import { RARITY_IDS } from "@highmarch/domain";
import { icon } from "./iconsKit";
import { closeBtnHtml } from "./kit";
import { uiUrl } from "./portraits";
import { currencyIcon } from "./shops/shopsArt";

/**
 * Design-system v2 markup (m6; the spec is the project's UI style doc, the CSS is styles/components.css, the tokens are
 * the v2 block of styles/tokens.css). Every screen builds its chrome from these — sheet header, section header, card
 * flag, item well, shop slot, price button, VIP XP bubble, value burst, timer chip, tags, lock chip, art hero, icon
 * tabs, list row, empty state — so a shop, the Armoury and the hub read as one game. Markup only (strings, no DOM, no
 * game rules); the caller owns events. Text is trusted game copy (like kit.ts); `esc` guards the attribute values.
 */

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" })[c]!);
const attr = (name: string, v: string | number | undefined | null): string => (v === undefined || v === null || v === "" ? "" : ` ${name}="${esc(String(v))}"`);
const isUrl = (s: string): boolean => /^(\.|\/|data:|blob:|https?:)/.test(s);
/** An image URL as an <img>, anything else (inline SVG, a painted-icon <img>) as given. */
const art = (s: string, cls = ""): string => (isUrl(s) ? `<img${cls ? ` class="${cls}"` : ""} decoding="async" src="${s}" alt="">` : s);

export type RarityKey = (typeof RARITY_IDS)[number];
/** A rarity index (0 common … 5 mythic) as its CSS key, clamped: the class suffix of `.k-well.r-<key>`. */
export function rarityKey(r: number): RarityKey {
  const i = Math.max(0, Math.min(RARITY_IDS.length - 1, Math.floor(Number.isFinite(r) ? r : 0)));
  return RARITY_IDS[i];
}

/* ---- sheet header -------------------------------------------------------------------------------------------- */

/** The portrait tile's background: navy well (default), the old blue, gold, ember, plum, wood. */
export type HeadTint = "navy" | "blue" | "gold" | "ember" | "plum" | "wood";
export interface SheetHeadOpts {
  readonly title: string;
  readonly sub?: string;
  /** The portrait: an image URL, or markup (a painted icon, a kit SVG). */
  readonly portrait: string;
  readonly tint?: HeadTint;
  /** Right-hand extras before the close button: currency pills (currencyPillsHtml), a VIP chip. */
  readonly extra?: string;
  /** Default true: the 44 px close (×) icon button, `.close`. */
  readonly close?: boolean;
}

/** Every sheet's header: portrait tile · Lilita title over a muted line · extras · close. Class `.sheethead.k-head`. */
export function sheetHeadHtml(o: SheetHeadOpts): string {
  const close = o.close === false ? "" : closeBtnHtml();
  return `<div class="sheethead k-head"><div class="portrait t-${o.tint ?? "navy"}">${art(o.portrait)}</div>`
    + `<div class="titles"><b class="disp">${o.title}</b>${o.sub ? `<small>${o.sub}</small>` : ""}</div>`
    + `${o.extra ? `<div class="k-extra">${o.extra}</div>` : ""}${close}</div>`;
}

/**
 * The currencies a sheet spends, as one quiet line under its title (shop redesign 2026-10-19: the hub's top bar shows
 * gems only; every other currency shows on the screen that uses it). `data` keys let a screen roll one after a buy.
 */
export function currencyLineHtml(items: readonly { readonly icon: string; readonly amount: string; readonly key: string; readonly plus?: boolean }[]): string {
  return `<span class="k-curline">${items.map((c) => `<span class="k-cl" data-cur="${esc(c.key)}">${art(c.icon)}<b>${c.amount}</b>${c.plus ? `<i class="k-plus" aria-hidden="true">+</i>` : ""}</span>`).join("")}</span>`;
}

/** Currency pills for a sheet header: the hub's navy `.pill` with each currency's painted icon. */
export type CurrencyKey = "gems" | "marks" | "gold";
export function currencyPillsHtml(pills: readonly { currency: CurrencyKey; amount: string | number }[]): string {
  return `<div class="k-pills">${pills.map((p) => `<div class="pill k-cur-${p.currency}" data-cur="${p.currency}"><div class="ic img">${currencyIcon(p.currency, 24)}</div><span>${p.amount}</span></div>`).join("")}</div>`;
}

/* ---- section header, kicker, card flag ------------------------------------------------------------------------- */

/** A section header: Lilita title (optional leading icon markup) and a muted note on the right. */
export function secHtml(title: string, note = "", lead = ""): string {
  return `<div class="k-sec"><b>${lead}${title}</b>${note ? `<small>${note}</small>` : ""}</div>`;
}

/** The small uppercase label with a hairline ("LEVEL 25 LINE-UP", "WHERE IT HELPS"); `note` sits after the rule. */
export function kickerHtml(text: string, note = ""): string {
  return `<div class="k-kicker">${text}${note ? `<small>${note}</small>` : ""}</div>`;
}

/** An offer card's title: a notched ribbon on the card's top-left corner (gold, or navy for a quieter card). */
export function flagHtml(text: string, variant: "gold" | "navy" = "gold"): string {
  return `<div class="k-flag${variant === "navy" ? " navy" : ""} disp">${text}</div>`;
}

/* ---- item well and shop slot ----------------------------------------------------------------------------------- */

export interface WellOpts {
  /** The item's art: an image URL or markup. */
  readonly art: string;
  /** A rarity index for chests and gear (the jewel tint); null/undefined = the neutral navy well (resources, gems, speed-ups). */
  readonly rarity?: number | null;
  readonly size?: "sm" | "md" | "lg";
  /** Bottom-right count ("×2", "1,038"). */
  readonly count?: string | number;
  /** Top cap (a speed-up's length: "8h"). */
  readonly cap?: string;
  /** A "?" badge: what it can hold (odds) or a pick still to make. */
  readonly info?: boolean;
  readonly dim?: boolean;
  readonly className?: string;
  /** Accessible name when the well stands alone as a button. */
  readonly label?: string;
  readonly tag?: "div" | "button" | "span";
  readonly data?: string;
}

/** An item well: a jewel-dark rounded square with a gold line, the art, its count and cap. */
export function wellHtml(o: WellOpts): string {
  const tag = o.tag ?? "div";
  const cls = ["k-well", o.size && o.size !== "md" ? o.size : "", o.rarity === undefined || o.rarity === null ? "" : `r-${rarityKey(o.rarity)}`, o.dim ? "dim" : "", o.className ?? ""].filter(Boolean).join(" ");
  // owner 2026-10-21: never "×1" (one of a thing shows no count)
  const count = o.count === undefined || o.count === "" || /^[×x]\s?1$/.test(String(o.count).trim()) ? "" : `<b class="k-count">${o.count}</b>`;
  return `<${tag}${tag === "button" ? ` type="button"` : ""} class="${cls}"${attr("aria-label", o.label)}${o.data ? ` ${o.data}` : ""}>${art(o.art, "k-art")}`
    + `${o.cap ? `<i class="k-cap">${o.cap}</i>` : ""}${count}</${tag}>`;   // no "?" (owner 2026-10-21): a tap shows the item in focus, with its odds
}

export type SlotState = "ok" | "sold" | "locked";
export interface SlotTileOpts {
  readonly well: WellOpts;
  readonly name?: string;
  /** "Left: 1". */
  readonly left?: string;
  /** The action (priceButtonHtml) when buyable. */
  readonly price?: string;
  /** A burst (burstHtml) on the corner. */
  readonly burst?: string;
  readonly state?: SlotState;
  /** The lock chip's words when locked ("VIP 4"). */
  readonly lock?: string;
  /** Featured (the Event shop's -50 % row): gold card. */
  readonly featured?: boolean;
  /** Extra classes on the tile (screen hooks: `stile s-ok`, `gpack`). */
  readonly className?: string;
  readonly data?: string;
  /** The stamp of a sold tile ("Sold out" by default; "Claimed" on a free one). */
  readonly stamp?: string;
}

/** A shop slot: the well, "Left: n", the price; sold out shows a stamp, locked a lock chip. */
export function slotTileHtml(o: SlotTileOpts): string {
  const state = o.state ?? "ok";
  const dim = state !== "ok";
  const action = state === "sold" ? `<span class="k-stamp">${o.stamp ?? "Sold out"}</span>` : state === "locked" ? lockChipHtml(o.lock ?? "Locked") : (o.price ?? "");
  return `<div class="k-tile${o.featured ? " hl" : ""}${state !== "ok" ? ` is-${state}` : ""}${o.className ? ` ${o.className}` : ""}"${o.data ? ` ${o.data}` : ""}>${o.burst ?? ""}${wellHtml({ ...o.well, dim: o.well.dim || dim })}`
    + `${o.name ? `<span class="k-name">${o.name}</span>` : ""}${o.left ? `<small class="k-left">${o.left}</small>` : ""}${action}</div>`;
}

/* ---- price button, VIP XP bubble, value burst ------------------------------------------------------------------ */

/** What a price is in: a store price string (real money) or one of the game's currencies. */
export type PriceCurrency = "real" | CurrencyKey;
export interface PriceOpts {
  readonly currency: PriceCurrency;
  /** The amount ("1,892") or, for real money, the store's localized price ("$4.99"). */
  readonly amount: string | number;
  /** A word before the price ("BUY", "Refresh"). */
  readonly label?: string;
  /** Struck-through was-price above the button. */
  readonly was?: string;
  /** VIP XP the purchase pays (the bubble); 0/undefined = none. */
  readonly vipXp?: number;
  /** The player cannot afford it: the amount turns red (the tap still explains). */
  readonly short?: boolean;
  readonly disabled?: boolean;
  /** Where the VIP XP bubble sits: the top-right shoulder (default) or the top-left, when a burst owns the right. */
  readonly bubble?: "right" | "left";
  /** The was-price under the button instead of above it (a featured card whose flag owns the space above). */
  readonly wasBelow?: boolean;
  readonly className?: string;
  /** Raw attributes (`data-offer="…"`). */
  readonly data?: string;
}

/** The one price button: gold (it is the primary action), the currency's painted icon, the amount in Lilita. */
export function priceButtonHtml(o: PriceOpts): string {
  const cur = o.currency === "real" ? "" : `<span class="k-cur">${currencyIcon(o.currency, 22)}</span>`;
  const cls = ["btn", "gold", "k-price", o.short ? "short" : "", o.disabled ? "dis" : "", o.bubble === "left" ? "bub-l" : "", o.wasBelow ? "was-b" : "", o.className ?? ""].filter(Boolean).join(" ");
  return `<button type="button" class="${cls}"${o.disabled ? " disabled" : ""}${o.data ? ` ${o.data}` : ""}>`
    + `${o.label ? `<span class="k-lbl">${o.label}</span>` : ""}${cur}<span class="k-amt">${o.amount}</span>`
    + `${o.was ? `<s class="k-was">${o.was}</s>` : ""}${o.vipXp ? xpBubbleHtml(o.vipXp) : ""}</button>`;
}

/** The VIP XP a purchase pays: a navy chip with the VIP crown and "+500". */
export function xpBubbleHtml(xp: number): string {
  return `<span class="k-bubble" aria-label="+${xp.toLocaleString("en-US")} VIP XP"><img decoding="async" src="${uiUrl("icons/vip_points.png")}" alt="">+${xp.toLocaleString("en-US")}</span>`;
}

/** The value burst ("621%" / VALUE, the honest value) or the discount burst ("-20%"): one red seal, gold rim. */
export function burstHtml(text: string, kind: "value" | "off" = "value"): string {
  return kind === "value"
    ? `<span class="k-burst" aria-label="${esc(text)} value"><b>${text}</b><small>VALUE</small></span>`
    : `<span class="k-burst off" aria-label="${esc(text)} off"><b>${text}</b></span>`;
}

/* ---- timers, tags, lock ------------------------------------------------------------------------------------------- */

export interface TimerOpts {
  /** Words before the time ("New stock in"). */
  readonly label?: string;
  /** Unix seconds the countdown ends: `data-ends`, ticked by tickTimers. */
  readonly endsAt?: number;
  /** Muted text on parchment instead of the navy pill. */
  readonly inline?: boolean;
  /** Under an hour left: the clock turns red. */
  readonly urgent?: boolean;
}

/** A countdown chip: navy pill, gold clock, tabular time (`.ct`, which tickTimers rewrites). */
export function timerChipHtml(text: string, o: TimerOpts = {}): string {
  const cls = ["k-timer", o.inline ? "inline" : "", o.urgent ? "urgent" : ""].filter(Boolean).join(" ");
  return `<span class="${cls}"${attr("data-ends", o.endsAt)}>${icon("clock", 15)}${o.label ? `<span class="k-lbl">${o.label}</span>` : ""}<b class="ct">${text}</b></span>`;
}

/**
 * Rewrites every `[data-ends]` timer's `.ct` under `root` with `fmt(seconds left)`; true when one has run out (the
 * caller redraws: a restock, a new day). One ticker for Deals, VIP and the Shops.
 */
export function tickTimers(root: ParentNode, now: number, fmt: (secondsLeft: number) => string): boolean {
  let ended = false;
  for (const c of root.querySelectorAll<HTMLElement>("[data-ends]")) {
    const left = Number(c.dataset.ends) - now;
    if (left <= 0) ended = true;
    const t = c.querySelector(".ct");
    if (t) t.textContent = fmt(Math.max(0, left));
  }
  return ended;
}

export type TagKind = "new" | "up" | "info" | "best";
const TAG_TEXT: Record<TagKind, string> = { new: "NEW", up: "UP", info: "INFO", best: "BEST" };
/** A small tag: NEW (red; unseen), UP (green; a raised stat), INFO (navy), BEST (gold; the best value). */
export function tagHtml(kind: TagKind, text = TAG_TEXT[kind]): string {
  return `<span class="k-tag ${kind}">${text}</span>`;
}

/** Why a thing is not yours yet: a navy chip with a gold padlock ("VIP 4", "Lv 20"). */
export function lockChipHtml(text: string): string {
  return `<span class="k-lock">${icon("lock", 15)}${text}</span>`;
}

/** The "chosen" mark: a green disc with a white tick (a picked prize, a claimed track stop, the current choice). */
export function checkHtml(className = ""): string {
  return `<i class="k-check${className ? ` ${className}` : ""}" aria-hidden="true"></i>`;
}

export interface PickSlotOpts {
  /** Accessible name ("Pick 1"). */
  readonly label: string;
  readonly size?: "sm" | "md";
  readonly className?: string;
  readonly data?: string;
}

/** An empty pick slot: a dashed well with a gold "+", a button the player fills (Custom Chest, a Master's prize). */
export function pickSlotHtml(o: PickSlotOpts): string {
  const cls = ["k-well", o.size === "md" ? "" : "sm", "k-pick", o.className ?? ""].filter(Boolean).join(" ");
  return `<button type="button" class="${cls}"${attr("aria-label", o.label)}${o.data ? ` ${o.data}` : ""}><b aria-hidden="true">+</b></button>`;
}

/* ---- art hero, icon tabs, list row, empty state ------------------------------------------------------------------ */

export interface HeroOpts {
  /** The painted scene (URL): character + props, cover-fitted, focal point right. */
  readonly art: string;
  readonly title: string;
  /** Small gold line above the title ("LIVE EVENT", "TODAY: ARMOURER"). */
  readonly kicker?: string;
  readonly blurb?: string;
  /** A timer (timerChipHtml) under the title. */
  readonly timer?: string;
  /** Actions on the right (a Refresh price button, a Change button). */
  readonly side?: string;
  /** The art's focal point (CSS object-position), so the character's face stays in frame: default "70% 30%". */
  readonly focus?: string;
  readonly className?: string;
  /**
   * Shop redesign (2026-10-19, Kingshot-style): the bright banner instead of the painted night scene — a daylight
   * backdrop (`theme`), Captain Bram's cut-out (`cast`, an image URL) on the right and the page's prop (`prop`, an image
   * URL) beside him. The copy keeps the same slots (title, timer, side); the blurb and kicker are dropped.
   */
  readonly bright?: { readonly theme: BannerTheme; readonly cast: string; readonly prop?: string;
    /** 2026-10-20: the rendered daylight scene (art/interface/banners/banner_<page>.webp: the fortress on the hills,
     * Captain Bram on the right), which replaces the CSS backdrop and the cut-out. */
    readonly scene?: string };
}

/** The bright banners' daylight backdrops (styles/s3.css). */
export type BannerTheme = "sky" | "market" | "camp" | "royal" | "gold";

/** A character / art hero: the painted art in a navy plate with a gold rim, the copy on a navy scrim at the left. */
export function heroHtml(o: HeroOpts): string {
  if (o.bright) {
    const b = o.bright;
    const art = b.scene ? `<img class="k-scene" decoding="async" src="${b.scene}" alt="">` : `<img class="k-cast" decoding="async" src="${b.cast}" alt="">`;
    return `<section class="k-hero k-banner t-${b.theme}${b.scene ? " has-scene" : ""}${o.className ? ` ${o.className}` : ""}">${art}`
      + `${b.prop ? `<i class="k-glow" aria-hidden="true"></i><img class="k-prop" decoding="async" src="${b.prop}" alt="">` : ""}`
      + `<div class="k-hero-copy"><b>${o.title}</b>${o.timer ?? ""}${o.side ? `<div class="k-hero-side">${o.side}</div>` : ""}</div></section>`;
  }
  return `<section class="k-hero${o.className ? ` ${o.className}` : ""}"><img class="k-hero-art" decoding="async" src="${o.art}" alt=""${o.focus ? ` style="object-position:${esc(o.focus)}"` : ""}>`
    + `<div class="k-hero-copy">${o.kicker ? `<small>${o.kicker}</small>` : ""}<b>${o.title}</b>${o.timer ?? ""}${o.blurb ? `<p>${o.blurb}</p>` : ""}`
    // The action sits in the copy column, under the words: the art's right side (Captain Bram) stays clear (2026-10-01).
    + `${o.side ? `<div class="k-hero-side">${o.side}</div>` : ""}</div></section>`;
}

export interface IconTab {
  readonly key: string;
  readonly label: string;
  /** Painted icon URL. */
  readonly icon: string;
  /** Red count (0/undefined: none). */
  readonly dot?: number | string;
  /** Not open yet: the icon greys and a padlock disc sits top-left; the tab still takes the tap (it explains). */
  readonly locked?: boolean;
  /** Extra classes (screen hooks such as the Deals smoke's `dtab`). */
  readonly className?: string;
  /** Raw attributes (`data-page="starter"`). */
  readonly data?: string;
}

/**
 * Icon tabs: the segmented control's picture variant (navy tiles, the chosen one gold) for Deals pages and the Shops.
 * A `tablist` of `tab` buttons with `data-k`, class `kseg` — kit.ts `bindSeg` wires taps and arrow keys as it does for
 * segHtml. `fill`: the tabs share the width (3 shops) instead of scrolling (10 Deals pages).
 */
export function iconTabsHtml(tabs: readonly IconTab[], activeKey: string, label = "", fill = false): string {
  const one = (t: IconTab): string => {
    const on = t.key === activeKey;
    const dot = t.dot !== undefined && t.dot !== 0 && t.dot !== "" ? `<span class="dot">${t.dot}</span>` : "";
    const cls = ["k-itab", on ? "on" : "", t.locked ? "is-locked" : "", t.className ?? ""].filter(Boolean).join(" ");
    const lock = t.locked ? `<i class="k-itlk" aria-hidden="true">${icon("lock", 12)}</i>` : "";
    return `<button type="button" role="tab" class="${cls}" data-k="${esc(t.key)}"${t.data ? ` ${t.data}` : ""} aria-selected="${on}" tabindex="${on ? 0 : -1}"><img decoding="async" src="${t.icon}" alt=""><span class="k-itl">${t.label}</span>${lock}${dot}</button>`;
  };
  return `<div class="k-itabs kseg${fill ? " fill" : ""}" role="tablist"${attr("aria-label", label)}>${tabs.map(one).join("")}</div>`;
}

export interface RowOpts {
  /** Leading art: URL or markup (a well, a painted icon). */
  readonly lead?: string;
  readonly title: string;
  readonly sub?: string;
  /** Trailing markup (a button, a chevron, a tag). */
  readonly end?: string;
  /** The whole row is one button (a list that opens something). */
  readonly tap?: boolean;
  /** The 44 px row (end-screen extras, a dense list): smaller lead art and padding. */
  readonly compact?: boolean;
  /** Class on the title (`disp` for a Lilita headline). */
  readonly titleClass?: string;
  readonly className?: string;
  readonly data?: string;
}

/** A list row on a sheet: a cream card with the lead art, title over a muted line, and the end. */
export function rowHtml(o: RowOpts): string {
  const tag = o.tap ? "button" : "div";
  return `<${tag}${o.tap ? ` type="button"` : ""} class="k-row${o.tap ? " tap" : ""}${o.compact ? " compact" : ""}${o.className ? ` ${o.className}` : ""}"${o.data ? ` ${o.data}` : ""}>${o.lead ? art(o.lead) : ""}`
    + `<span class="k-row-t"><b${o.titleClass ? ` class="${o.titleClass}"` : ""}>${o.title}</b>${o.sub ? `<small>${o.sub}</small>` : ""}</span>${o.end ? `<span class="k-row-end">${o.end}</span>` : ""}</${tag}>`;
}

/** An empty state: a dashed parchment well with art and one line saying what fills it. */
export function emptyHtml(artUrlOrHtml: string, caption: string): string {
  return `<div class="k-empty">${art(artUrlOrHtml)}<span>${caption}</span></div>`;
}
